/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { EcsperApiError } from '@ecsper/api-client';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';

import type { AppContext } from '../bootstrap.js';
import { ReportsDashboard } from './ReportsDashboard.js';

export interface ReportPickerProps {
  readonly ctx: ReportPickerContext;
  readonly matterId: string | null;
  readonly onReportSelected: (reportId: string) => void;
}

/** Subset of {@link AppContext} required for listing reports (keeps tests simple). */
export type ReportPickerContext = Pick<AppContext, 'apiClient'>;

interface ReportRow {
  readonly id: string;
  readonly title: string;
  readonly lifecycleState: string;
  readonly updatedAt: string;
}

function parseReportListPayload(data: unknown): ReportRow[] {
  if (data === null || typeof data !== 'object') {
    return [];
  }
  const root = data as Record<string, unknown>;
  const dataField = root['data'];
  const inner =
    dataField !== undefined &&
    typeof dataField === 'object' &&
    dataField !== null &&
    !Array.isArray(dataField)
      ? (dataField as Record<string, unknown>)
      : root;
  const rawItems: unknown[] =
    (Array.isArray(inner['items']) ? inner['items'] : undefined) ??
    (Array.isArray(inner['results']) ? inner['results'] : undefined) ??
    (Array.isArray(root['results']) ? root['results'] : undefined) ??
    [];

  const rows: ReportRow[] = [];
  for (const item of rawItems) {
    if (item === null || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const id = row['id'];
    const title = row['title'];
    if (typeof id !== 'string' || id === '' || typeof title !== 'string') continue;
    const lifecycleState =
      typeof row['lifecycle_state'] === 'string'
        ? row['lifecycle_state']
        : typeof row['lifecycleState'] === 'string'
          ? row['lifecycleState']
          : '—';
    const updatedAt =
      typeof row['updated_at'] === 'string'
        ? row['updated_at']
        : typeof row['updatedAt'] === 'string'
          ? row['updatedAt']
          : '';
    rows.push({ id, title, lifecycleState, updatedAt });
  }
  return rows;
}

function formatUpdated(iso: string): string {
  if (iso === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Lists reports for a matter (CTR-MS-WK-REPORT-001:
 * `GET /api/v1/workspace/expert/reports/?item_id={uuid}`) so the add-in can run
 * without a `reportId` in the task pane URL.
 */
export function ReportPicker(props: ReportPickerProps): ReactElement {
  const { ctx, matterId, onReportSelected } = props;
  const [phase, setPhase] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [rows, setRows] = useState<readonly ReportRow[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const load = useCallback(async (): Promise<void> => {
    if (matterId === null || matterId === '') {
      return;
    }
    setPhase('loading');
    setErrorMessage(undefined);
    try {
      const path = '/api/v1/workspace/expert/reports/';
      const json = await ctx.apiClient.get<unknown>(path, {
        params: { item_id: matterId, page: 1, page_size: 50 },
      });
      setRows(parseReportListPayload(json));
      setPhase('ready');
    } catch (e) {
      const msg =
        e instanceof EcsperApiError ? e.message : e instanceof Error ? e.message : String(e);
      setErrorMessage(msg);
      setPhase('error');
    }
  }, [ctx.apiClient, matterId]);

  useEffect(() => {
    if (matterId === null || matterId === '') {
      setPhase('idle');
      setRows([]);
      return;
    }
    void load();
  }, [load, matterId]);

  const handleRefresh = (): void => {
    void load();
  };

  if (matterId === null || matterId === '') {
    return <ReportsDashboard ctx={ctx} />;
  }

  if (phase === 'loading' || phase === 'idle') {
    return (
      <div className="ecs-sidebar" role="status" aria-busy="true" aria-label="Loading reports">
        <p className="ecs-sidebar__title">Reports</p>
        <ul className="ecs-sidebar__list">
          {[0, 1, 2].map((i) => (
            <li key={i} className="ecs-skeleton ecs-skeleton--row" aria-hidden="true" />
          ))}
        </ul>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="ecs-sidebar" role="alert">
        <div className="ecs-error">
          <p className="ecs-error__message">Could not load reports.</p>
          {errorMessage !== undefined ? <p className="ecs-card__error">{errorMessage}</p> : null}
          <button type="button" className="ecs-btn ecs-btn--secondary" onClick={handleRefresh}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="ecs-sidebar" role="status">
        <p className="ecs-sidebar__empty-text">No reports found in this matter.</p>
      </div>
    );
  }

  return (
    <div className="ecs-sidebar">
      <p className="ecs-sidebar__title">Choose a report</p>
      <ul role="list" className="ecs-sidebar__list">
        {rows.map((row) => (
          <li key={row.id} role="listitem">
            <button
              type="button"
              onClick={() => {
                onReportSelected(row.id);
              }}
              className="ecs-card ecs-card--interactive"
            >
              <span className="ecs-card__group-main">
                <span className="ecs-card__title">{row.title}</span>
                <span className="ecs-card__ref">
                  Status: {row.lifecycleState} · Last modified: {formatUpdated(row.updatedAt)}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
